<template>
  <div class="ppt-slides">
    <!-- 列表页 -->
    <template v-if="!editProject">
      <div class="ppt-header">
        <h2 class="ppt-title"><i class="el-icon-picture-outline"></i> 背景图片设计</h2>
        <p class="ppt-desc">选择项目后可在两页图上设计文字并导出为 2 张 JPG。</p>
      </div>
      <div class="ppt-list-toolbar">
        <el-input
          v-model.trim="searchKeyword"
          size="small"
          clearable
          placeholder="搜索项目名称"
          class="ppt-search-input"
        >
          <i slot="prefix" class="el-icon-search"></i>
        </el-input>
      </div>
      <div v-loading="loading" class="ppt-list-wrap">
        <div v-if="filteredList.length" ref="listScroll" class="ppt-list-scroll" @scroll="onListScroll">
          <div class="ppt-list-grid">
            <div
              v-for="item in visibleList"
              :key="item.name"
              class="ppt-list-item"
              @click="openEdit(item)"
            >
              <img
                v-if="getListImageUrl(item)"
                :src="fullUrl(getListImageUrl(item))"
                :alt="item.name"
                loading="lazy"
                @error="onImgError($event, item)"
              />
              <div class="ppt-list-name">
                <i class="el-icon-picture-outline ppt-list-icon"></i>
                <span>{{ item.name }}</span>
              </div>
            </div>
          </div>
          <p v-if="hasMoreList" class="ppt-load-more">向下滚动加载更多</p>
        </div>
        <p v-else-if="!loading" class="ppt-empty">{{ emptyStateText }}</p>
      </div>
    </template>

    <!-- 编辑页 -->
    <template v-else>
      <div class="ppt-edit-header">
        <el-button size="small" icon="el-icon-arrow-left" @click="backToList">返回列表</el-button>
        <span class="ppt-edit-title">{{ editProject.name }}</span>
      </div>
      <div class="ppt-edit-body">
        <el-tabs v-model="activeSlideTab" type="card" class="ppt-slide-tabs">
          <el-tab-pane v-for="slideIndex in [0, 1]" :key="slideIndex" :name="String(slideIndex)" :label="slideIndex === 0 ? '欢迎界面' : '答题界面'">
            <div class="ppt-slide-block" v-if="slides[slideIndex]">
              <div class="ppt-slide-row">
            <div class="ppt-preview-wrap" :class="{ 'ppt-preview-wrap-split': slideIndex === 0 && editProject.coverUrl }">
              <template v-if="slideIndex === 0 && editProject.coverUrl">
                <div class="ppt-preview-split">
                  <div class="ppt-cover-example">
                    <span class="ppt-cover-label">示例（封面）</span>
                    <img :src="fullUrl(getCoverExampleUrl(editProject))" alt="封面示例" class="ppt-preview-img ppt-preview-img-half" title="点击全屏预览" @error="onImgError($event, editProject)" @click="openCoverPreview" />
                  </div>
                  <div class="ppt-preview-inner ppt-preview-inner-half">
                    <img
                      :ref="'slideImg' + slideIndex"
                      :src="fullUrl(slides[slideIndex].url)"
                      alt="欢迎界面"
                      class="ppt-preview-img"
                      @load="onSlideImgLoad(slideIndex)"
                      @click="openSlidePreview(slideIndex)"
                    />
                    <div
                      v-for="(box, boxIndex) in slides[slideIndex].boxes"
                      :key="'overlay-' + slideIndex + '-' + boxIndex + '-' + (box.colorMode || 'solid') + '-' + (box.gradientStart || '') + '-' + (box.gradientEnd || '')"
                      class="ppt-text-overlay"
                      :style="overlayBoxStyle(box, slideIndex)"
                    >
                      <span v-if="isTextGradient(box)" class="ppt-text-overlay-inner" :style="overlayTextGradientStyle(box)">{{ box.content || '（输入文字）' }}</span>
                      <template v-else>{{ box.content || '（输入文字）' }}</template>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="ppt-preview-inner" :style="previewSizeStyle(slideIndex)">
                  <img
                    :ref="'slideImg' + slideIndex"
                    :src="fullUrl(slides[slideIndex].url)"
                    :alt="slideIndex === 0 ? '欢迎界面' : '答题界面'"
                    class="ppt-preview-img"
                    @load="onSlideImgLoad(slideIndex)"
                    @click="openSlidePreview(slideIndex)"
                  />
                  <div
                    v-for="(box, boxIndex) in slides[slideIndex].boxes"
                    :key="'overlay-' + slideIndex + '-' + boxIndex + '-' + (box.colorMode || 'solid') + '-' + (box.gradientStart || '') + '-' + (box.gradientEnd || '')"
                    class="ppt-text-overlay"
                    :style="overlayBoxStyle(box, slideIndex)"
                  >
                    <span v-if="isTextGradient(box)" class="ppt-text-overlay-inner" :style="overlayTextGradientStyle(box)">{{ box.content || '（输入文字）' }}</span>
                    <template v-else>{{ box.content || '（输入文字）' }}</template>
                  </div>
                </div>
              </template>
              <div class="ppt-download-row">
                <el-button size="small" type="default" icon="el-icon-upload2" @click="triggerLoadBg(slideIndex)">加载背景图</el-button>
                <input :ref="'fileBg' + slideIndex" type="file" accept="image/*" class="ppt-file-input" @change="onBgFileChange(slideIndex, $event)" />
                <el-button size="small" type="primary" icon="el-icon-download" @click="exportOneJpg(slideIndex)">下载</el-button>
                <el-button size="small" type="default" icon="el-icon-document-copy" @click="copySlideToClipboard(slideIndex)">复制图片</el-button>
                <el-button size="small" type="default" icon="el-icon-document-copy" @click="copySlideBase64(slideIndex)">复制 Base64</el-button>
              </div>
              <p class="ppt-download-hint">顶伯知识竞赛软件、顶伯评委评分软件、顶伯触摸屏答题软件支持 Base64 粘贴，其它软件建议下载图片后使用。</p>
            </div>
            <div class="ppt-form-wrap">
              <div class="ppt-box-count">
                <span>本页文本框数量：</span>
                <el-select v-model="slides[slideIndex].boxCount" size="small" style="width: 80px" @change="changeBoxCount(slideIndex)">
                  <el-option :value="1" label="1"></el-option>
                  <el-option :value="2" label="2"></el-option>
                  <el-option :value="3" label="3"></el-option>
                  <el-option :value="4" label="4"></el-option>
                </el-select>
              </div>
                <el-tabs v-model="activeBoxTab[slideIndex]" type="card" class="ppt-box-tabs">
                <el-tab-pane
                  v-for="(box, boxIndex) in slides[slideIndex].boxes"
                  :key="boxIndex"
                  :name="String(boxIndex)"
                  :label="'框 ' + (boxIndex + 1)"
                >
                  <div class="ppt-box-form">
                    <div class="option-row">
                      <span class="option-label"><i class="el-icon-edit-outline"></i> 文字内容</span>
                      <el-input v-model="box.content" type="textarea" :rows="2" placeholder="文字内容" size="small" class="ppt-input-text"></el-input>
                    </div>
                    <div class="option-row option-row-slider">
                      <span class="option-label"><i class="el-icon-s-operation"></i> 字号</span>
                      <div class="slider-wrap">
                        <el-slider v-model="box.fontSize" :min="12" :max="120" :show-input="true" size="small"></el-slider>
                      </div>
                    </div>
                    <div class="option-row">
                      <span class="option-label"><i class="el-icon-brush"></i> 类型</span>
                      <el-radio-group v-model="box.colorMode" size="small">
                        <el-radio-button label="solid">纯色</el-radio-button>
                        <el-radio-button label="gradient">渐变</el-radio-button>
                      </el-radio-group>
                    </div>
                    <div class="option-row option-row-presets">
                      <span class="option-label"><i class="el-icon-magic-stick"></i> {{ box.colorMode === 'solid' ? '纯色预设' : '渐变预设' }}</span>
                      <div class="preset-list">
                        <template v-if="box.colorMode === 'solid'">
                          <div
                            v-for="(p, i) in solidPresets"
                            :key="'s-' + i"
                            class="preset-item"
                            :style="{ background: p.gradientCss }"
                            :title="p.name"
                            @click="applySolidPreset(slideIndex, boxIndex, p)"
                          ></div>
                        </template>
                        <template v-else>
                          <div
                            v-for="(p, i) in gradientPresets"
                            :key="'g-' + i"
                            class="preset-item"
                            :style="{ background: p.gradientCss }"
                            :title="p.name"
                            @click="applyGradientPreset(slideIndex, boxIndex, p)"
                          ></div>
                        </template>
                      </div>
                    </div>
                    <template v-if="box.colorMode === 'solid'">
                      <div class="option-row">
                        <span class="option-label"><i class="el-icon-edit-outline"></i> 颜色</span>
                        <el-color-picker v-model="box.color" size="small" @active-change="val => val && $set(box, 'color', val)"></el-color-picker>
                        <el-input v-model="box.color" size="small" class="input-color"></el-input>
                      </div>
                    </template>
                    <template v-else>
                      <div class="option-row">
                        <span class="option-label"><i class="el-icon-sort"></i> 渐变方向</span>
                        <el-select v-model="box.gradientDirection" size="small" class="input-select">
                          <el-option label="从左到右" value="to right"></el-option>
                          <el-option label="从右到左" value="to left"></el-option>
                          <el-option label="从上到下" value="to bottom"></el-option>
                          <el-option label="从下到上" value="to top"></el-option>
                          <el-option label="左上到右下" value="to bottom right"></el-option>
                          <el-option label="右下到左上" value="to top left"></el-option>
                        </el-select>
                      </div>
                      <div class="option-row">
                        <span class="option-label"><i class="el-icon-edit"></i> 起始色</span>
                        <el-color-picker v-model="box.gradientStart" size="small" @active-change="val => val && $set(box, 'gradientStart', val)"></el-color-picker>
                        <el-input v-model="box.gradientStart" size="small" class="input-color"></el-input>
                      </div>
                      <div class="option-row">
                        <span class="option-label"><i class="el-icon-edit"></i> 结束色</span>
                        <el-color-picker v-model="box.gradientEnd" size="small" @active-change="val => val && $set(box, 'gradientEnd', val)"></el-color-picker>
                        <el-input v-model="box.gradientEnd" size="small" class="input-color"></el-input>
                      </div>
                    </template>
                    <div class="option-row">
                      <span class="option-label"><i class="el-icon-tickets"></i> 字体</span>
                      <el-select v-model="box.fontFamily" size="small" class="ppt-font-select" placeholder="字体">
                        <el-option v-for="f in fontOptions" :key="f.value" :label="f.label" :value="f.value"></el-option>
                      </el-select>
                    </div>
                    <div class="option-row">
                      <span class="option-label"><i class="el-icon-document"></i> 样式</span>
                      <el-checkbox v-model="box.bold">粗体</el-checkbox>
                      <el-checkbox v-model="box.italic">斜体</el-checkbox>
                    </div>
                    <div class="option-row option-row-slider">
                      <span class="option-label"><i class="el-icon-edit"></i> 描边</span>
                      <div class="slider-wrap">
                        <el-slider v-model="box.strokeWidth" :min="0" :max="10" :step="0.01" :show-input="true" size="small"></el-slider>
                      </div>
                    </div>
                    <div class="option-row" v-if="(box.strokeWidth || 0) > 0">
                      <span class="option-label"><i class="el-icon-edit"></i> 描边色</span>
                      <el-color-picker v-model="box.strokeColor" size="small" @active-change="val => val && $set(box, 'strokeColor', val)"></el-color-picker>
                      <el-input v-model="box.strokeColor" size="small" class="input-color"></el-input>
                    </div>
                    <div class="option-row">
                      <span class="option-label"><i class="el-icon-s-operation"></i> 位置</span>
                      <el-button size="mini" @click="centerBoxX(slideIndex, boxIndex)">水平居中</el-button>
                      <el-button size="mini" @click="centerBoxY(slideIndex, boxIndex)">垂直居中</el-button>
                    </div>
                    <div class="option-row option-row-slider">
                      <span class="option-label"><i class="el-icon-rank"></i> 位置 X%</span>
                      <div class="slider-wrap">
                        <el-slider v-model="box.xPercent" :min="0" :max="100" :show-input="true" size="small"></el-slider>
                      </div>
                    </div>
                    <div class="option-row option-row-slider">
                      <span class="option-label"><i class="el-icon-rank"></i> 位置 Y%</span>
                      <div class="slider-wrap">
                        <el-slider v-model="box.yPercent" :min="0" :max="100" :show-input="true" size="small"></el-slider>
                      </div>
                    </div>
                  </div>
                </el-tab-pane>
              </el-tabs>
            </div>
          </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 封面示例全屏预览 -->
      <div v-if="previewImageUrl" class="ppt-preview-mask" @click.self="closeCoverPreview">
        <div class="ppt-fullscreen-wrap">
          <img :src="previewImageUrl" alt="封面预览" class="ppt-preview-full-img" @click="closeCoverPreview" />
          <el-button size="small" icon="el-icon-close" class="ppt-preview-close" @click="closeCoverPreview">关闭</el-button>
        </div>
      </div>
      <!-- 幻灯片全屏预览（含文字叠加） -->
      <div v-if="previewSlideUrl" class="ppt-preview-mask" @click.self="closeSlidePreview">
        <div class="ppt-fullscreen-wrap">
          <div class="ppt-fullscreen-inner">
            <img ref="fullscreenPreviewImg" :src="previewSlideUrl" alt="预览" class="ppt-preview-full-img" @click="closeSlidePreview" @load="updateFullscreenFontScale" />
            <template v-if="slides[previewSlideIndex]">
              <div
                v-for="(box, boxIndex) in slides[previewSlideIndex].boxes"
                :key="'fs-' + boxIndex"
                class="ppt-text-overlay ppt-text-overlay-fullscreen"
                :style="overlayBoxStyle(box, previewSlideIndex, true)"
              >
                <span v-if="isTextGradient(box)" class="ppt-text-overlay-inner" :style="overlayTextGradientStyle(box, true)">{{ box.content || '' }}</span>
                <template v-else>{{ box.content || '' }}</template>
              </div>
            </template>
          </div>
          <el-button size="small" icon="el-icon-close" class="ppt-preview-close" @click="closeSlidePreview">关闭</el-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config.js'

const FONT_OPTIONS = [
  { label: '微软雅黑', value: 'Microsoft YaHei' },
  { label: '宋体', value: 'SimSun' },
  { label: '黑体', value: 'SimHei' },
  { label: '楷体', value: 'KaiTi' },
  { label: '仿宋', value: 'FangSong' },
  { label: '隶书', value: 'LiSu' },
  { label: '幼圆', value: 'YouYuan' },
  { label: '华文细黑', value: 'STXihei' },
  { label: '华文楷体', value: 'STKaiti' },
  { label: '华文宋体', value: 'STSong' },
  { label: '华文仿宋', value: 'STFangsong' },
  { label: '华文中宋', value: 'STZhongsong' },
  { label: '华文琥珀', value: 'STHupo' },
  { label: '华文新魏', value: 'STXinwei' },
  { label: '华文行楷', value: 'STXingkai' },
  { label: '方正舒体', value: 'FZShuTi' },
  { label: '方正姚体', value: 'FZYaoti' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Arial Black', value: 'Arial Black' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Verdana', value: 'Verdana' },
  { label: 'Tahoma', value: 'Tahoma' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS' },
  { label: 'Impact', value: 'Impact' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS' },
  { label: 'Courier New', value: 'Courier New' },
  { label: 'Consolas', value: 'Consolas' },
  { label: 'Segoe UI', value: 'Segoe UI' },
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'Palatino Linotype', value: 'Palatino Linotype' },
  { label: 'Lucida Sans Unicode', value: 'Lucida Sans Unicode' }
]

const defaultBox = () => ({
  content: '',
  fontSize: 28,
  color: '#000000',
  colorMode: 'solid',
  gradientStart: '#000000',
  gradientEnd: '#666666',
  gradientDirection: 'to right',
  fontFamily: 'STZhongsong',
  bold: true,
  italic: false,
  xPercent: 50,
  yPercent: 50,
  strokeWidth: 0,
  strokeColor: '#000000',
  shadowBlur: 0,
  shadowOffsetX: 0,
  shadowOffsetY: 0,
  shadowColor: 'rgba(0,0,0,0.5)'
})

export default {
  name: 'PptSlides',
  data() {
    return {
      list: [],
      loading: false,
      displayLimit: 24,
      searchKeyword: '',
      editProject: null,
      slides: [], // [{ url, boxCount, boxes: [defaultBox,...] }, ...]
      fontOptions: FONT_OPTIONS,
      previewSlideUrl: '',
      previewImageUrl: '',
      previewSlideIndex: 0,
      fullscreenFontScale: 1,
      slidePreviewHeights: { 0: 520, 1: 520 },
      activeSlideTab: '0',
      activeBoxTab: { 0: '0', 1: '0' },
      gradientPresets: [
        { name: '黄→橙', gradientStart: '#FFE066', gradientEnd: '#FF9F43', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #FFE066, #FF9F43)' },
        { name: '蓝→绿', gradientStart: '#409EFF', gradientEnd: '#67C23A', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #409EFF, #67C23A)' },
        { name: '粉→紫', gradientStart: '#FFB8D0', gradientEnd: '#B388FF', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #FFB8D0, #B388FF)' },
        { name: '青→蓝', gradientStart: '#26C6DA', gradientEnd: '#5C6BC0', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #26C6DA, #5C6BC0)' },
        { name: '橙→红', gradientStart: '#FF9800', gradientEnd: '#F44336', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #FF9800, #F44336)' },
        { name: '绿→青', gradientStart: '#66BB6A', gradientEnd: '#26A69A', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #66BB6A, #26A69A)' },
        { name: '紫→粉', gradientStart: '#7E57C2', gradientEnd: '#EC407A', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #7E57C2, #EC407A)' },
        { name: '深蓝→靛', gradientStart: '#1E88E5', gradientEnd: '#3949AB', gradientDirection: 'to bottom', gradientCss: 'linear-gradient(to bottom, #1E88E5, #3949AB)' },
        { name: '金→橙', gradientStart: '#FFD54F', gradientEnd: '#FF8F00', gradientDirection: 'to bottom', gradientCss: 'linear-gradient(to bottom, #FFD54F, #FF8F00)' },
        { name: '玫红→紫', gradientStart: '#F06292', gradientEnd: '#BA68C8', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #F06292, #BA68C8)' },
        { name: '薄荷→青', gradientStart: '#A7FFEB', gradientEnd: '#00BFA5', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #A7FFEB, #00BFA5)' },
        { name: '樱粉→桃', gradientStart: '#FCE4EC', gradientEnd: '#F48FB1', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #FCE4EC, #F48FB1)' },
        { name: '日落', gradientStart: '#FF6B6B', gradientEnd: '#FFE66D', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #FF6B6B, #FFE66D)' },
        { name: '海洋', gradientStart: '#667EEA', gradientEnd: '#764BA2', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #667EEA, #764BA2)' },
        { name: '森林', gradientStart: '#11998E', gradientEnd: '#38EF7D', gradientDirection: 'to bottom', gradientCss: 'linear-gradient(to bottom, #11998E, #38EF7D)' },
        { name: '暖灰', gradientStart: '#ECE9E6', gradientEnd: '#8E9EAB', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #ECE9E6, #8E9EAB)' },
        { name: '葡萄', gradientStart: '#4776E6', gradientEnd: '#8E54E9', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #4776E6, #8E54E9)' },
        { name: '珊瑚', gradientStart: '#FF7E5F', gradientEnd: '#FEB47B', gradientDirection: 'to right', gradientCss: 'linear-gradient(to right, #FF7E5F, #FEB47B)' }
      ],
      solidPresets: [
        { name: '浅蓝', color: '#B3E5FC', gradientCss: '#B3E5FC' },
        { name: '浅粉', color: '#F8BBD9', gradientCss: '#F8BBD9' },
        { name: '浅黄', color: '#FFF9C4', gradientCss: '#FFF9C4' },
        { name: '浅绿', color: '#C8E6C9', gradientCss: '#C8E6C9' },
        { name: '深蓝', color: '#1565C0', gradientCss: '#1565C0' },
        { name: '深灰', color: '#546E7A', gradientCss: '#546E7A' },
        { name: '黑色', color: '#000000', gradientCss: '#000000' },
        { name: '白色', color: '#FFFFFF', gradientCss: '#FFFFFF' },
        { name: '天蓝', color: '#4FC3F7', gradientCss: '#4FC3F7' },
        { name: '玫红', color: '#F06292', gradientCss: '#F06292' },
        { name: '橙色', color: '#FF9800', gradientCss: '#FF9800' },
        { name: '草绿', color: '#81C784', gradientCss: '#81C784' },
        { name: '紫色', color: '#BA68C8', gradientCss: '#BA68C8' },
        { name: '青色', color: '#4DD0E1', gradientCss: '#4DD0E1' },
        { name: '棕色', color: '#8D6E63', gradientCss: '#8D6E63' },
        { name: '红色', color: '#E57373', gradientCss: '#E57373' },
        { name: '金色', color: '#FFD54F', gradientCss: '#FFD54F' }
      ],
    }
  },
  computed: {
    filteredList() {
      const keyword = String(this.searchKeyword || '').trim().toLowerCase()
      if (!keyword) return this.list
      return this.list.filter(item => String(item && item.name ? item.name : '').toLowerCase().includes(keyword))
    },
    visibleList() {
      return this.filteredList.slice(0, this.displayLimit)
    },
    hasMoreList() {
      return this.displayLimit < this.filteredList.length
    },
    emptyStateText() {
      if (String(this.searchKeyword || '').trim()) return '未找到匹配项目'
      return '暂无项目，请确保 PPT制作背景 下存在 项目名/cover、1；如缺少 2，将自动复用 1。对应图片扩展名可为 jpg、jpeg、png、gif、webp、bmp、svg'
    }
  },
  watch: {
    searchKeyword() {
      this.displayLimit = 24
      this.$nextTick(() => {
        const listScroll = this.$refs.listScroll
        const el = Array.isArray(listScroll) ? listScroll[0] : listScroll
        if (el) el.scrollTop = 0
      })
    },
    '$route.params.name': {
      handler(name) {
        if (name && this.list.length) {
          const item = this.list.find(p => p.name === name)
          if (item) this.startEdit(item)
        }
      }
    }
  },
  mounted() {
    const name = this.$route.params.name
    if (name) {
      this.fetchList().then(() => {
        const item = this.list.find(p => p.name === name)
        if (item) this.startEdit(item)
      })
    } else {
      this.fetchList()
    }
  },
  methods: {
    fullUrl(path) {
      if (!path) return ''
      if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) return path
      return API_BASE_URL.replace(/\/$/, '') + path
    },
    fetchList() {
      this.loading = true
      return this.$axios.get('/ppt-slides/list')
        .then(res => {
          this.list = Array.isArray(res.data) ? res.data : []
        })
        .catch(() => { this.list = [] })
        .finally(() => { this.loading = false })
    },
    onListScroll(e) {
      const el = e.target
      if (!el) return
      const { scrollTop, clientHeight, scrollHeight } = el
      if (scrollTop + clientHeight < scrollHeight - 100) return
      if (this.displayLimit >= this.filteredList.length) return
      this.displayLimit = Math.min(this.displayLimit + 12, this.filteredList.length)
    },
    getListImageUrl(item) {
      if (!item) return ''
      return item.thumbUrl || item.coverUrl || ''
    },
    getCoverExampleUrl(item) {
      if (!item) return ''
      return item.thumbUrl || item.coverUrl || ''
    },
    onImgError(e, item) {
      const target = e && e.target ? e.target : null
      if (!target) return
      const coverUrl = this.fullUrl(item && item.coverUrl ? item.coverUrl : '')
      if (coverUrl && target.src !== coverUrl) {
        target.src = coverUrl
        return
      }
      target.style.display = 'none'
    },
    openEdit(item) {
      this.$router.push({ name: 'PptSlidesEdit', params: { name: item.name } })
      this.startEdit(item)
    },
    startEdit(item) {
      this.editProject = item
      this.slides = [
        { url: item.slide1Url, boxCount: 1, boxes: [defaultBox()] },
        { url: item.slide2Url, boxCount: 1, boxes: [defaultBox()] }
      ]
      this.slides.forEach(s => {
        s.boxes.forEach(b => {
          if (b.colorMode == null) b.colorMode = b.colorGradient ? 'gradient' : 'solid'
        })
      })
    },
    backToList() {
      this.editProject = null
      this.slides = []
      this.$router.push('/ppt-slides')
    },
    changeBoxCount(slideIndex) {
      const slide = this.slides[slideIndex]
      const count = slide.boxCount
      const boxes = [...slide.boxes]
      while (boxes.length < count) boxes.push(defaultBox())
      boxes.slice(0, count).forEach(b => {
        if (b.colorMode == null) b.colorMode = b.colorGradient ? 'gradient' : 'solid'
      })
      this.$set(this.slides[slideIndex], 'boxes', boxes.slice(0, count))
      const cur = parseInt(this.activeBoxTab[slideIndex], 10) || 0
      if (cur >= count) this.$set(this.activeBoxTab, slideIndex, String(Math.max(0, count - 1)))
    },
    isTextGradient(box) {
      return (box.colorMode === 'gradient' || box.colorGradient) && box.gradientStart != null && box.gradientEnd != null
    },
    applyGradientPreset(slideIndex, boxIndex, p) {
      const box = this.slides[slideIndex].boxes[boxIndex]
      if (!box) return
      this.$set(box, 'gradientStart', p.gradientStart != null ? p.gradientStart : '#000000')
      this.$set(box, 'gradientEnd', p.gradientEnd != null ? p.gradientEnd : '#666666')
      this.$set(box, 'gradientDirection', p.gradientDirection || 'to right')
      this.$set(box, 'color', 'transparent')
      this.$set(box, 'colorMode', 'gradient')
      this.$set(box, 'colorGradient', true)
      this.$message.success('已应用渐变：' + (p.name || ''))
    },
    applySolidPreset(slideIndex, boxIndex, p) {
      const box = this.slides[slideIndex].boxes[boxIndex]
      if (!box) return
      this.$set(box, 'colorMode', 'solid')
      this.$set(box, 'color', p.color != null ? p.color : '#000000')
      this.$set(box, 'colorGradient', false)
      this.$message.success('已应用纯色：' + (p.name || ''))
    },
    centerBoxX(slideIndex, boxIndex) {
      const box = this.slides[slideIndex].boxes[boxIndex]
      if (box) {
        this.$set(box, 'xPercent', 50)
        this.$message.success('已水平居中')
      }
    },
    centerBoxY(slideIndex, boxIndex) {
      const box = this.slides[slideIndex].boxes[boxIndex]
      if (box) {
        this.$set(box, 'yPercent', 50)
        this.$message.success('已垂直居中')
      }
    },
    openSlidePreview(slideIndex) {
      const slide = this.slides[slideIndex]
      if (slide && slide.url) {
        this.previewSlideIndex = slideIndex
        this.previewSlideUrl = this.fullUrl(slide.url)
        this.fullscreenFontScale = 1
        document.addEventListener('keydown', this._previewEscHandler)
        this.$nextTick(() => this.updateFullscreenFontScale())
      }
    },
    updateFullscreenFontScale() {
      if (!this.previewSlideUrl) return
      const idx = this.previewSlideIndex
      const fsEl = this.$refs.fullscreenPreviewImg
      const fsH = fsEl && fsEl.clientHeight
      if (!fsH || fsH <= 0) return
      const leftH = this.slidePreviewHeights[idx] || (() => {
        const ref = this.$refs['slideImg' + idx]
        const el = Array.isArray(ref) ? ref[0] : ref
        return (el && el.clientHeight > 0) ? el.clientHeight : 520
      })()
      this.fullscreenFontScale = fsH / leftH
    },
    closeSlidePreview() {
      this.previewSlideUrl = ''
      document.removeEventListener('keydown', this._previewEscHandler)
    },
    openCoverPreview() {
      if (!this.editProject || !this.editProject.coverUrl) return
      this.previewImageUrl = this.fullUrl(this.editProject.coverUrl)
      document.addEventListener('keydown', this._previewEscHandler)
    },
    closeCoverPreview() {
      this.previewImageUrl = ''
      document.removeEventListener('keydown', this._previewEscHandler)
    },
    _previewEscHandler(e) {
      if (e.key !== 'Escape') return
      if (this.previewImageUrl) this.closeCoverPreview()
      else if (this.previewSlideUrl) this.closeSlidePreview()
    },
    triggerLoadBg(slideIndex) {
      const ref = this.$refs['fileBg' + slideIndex]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (el) el.click()
    },
    onBgFileChange(slideIndex, e) {
      const file = e.target && e.target.files && e.target.files[0]
      if (!file || !file.type.startsWith('image/')) {
        this.$message.warning('请选择图片文件')
        e.target.value = ''
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        this.$set(this.slides[slideIndex], 'url', reader.result)
        this.$message.success('背景图已加载')
      }
      reader.readAsDataURL(file)
      e.target.value = ''
    },
    exportOneJpg(slideIndex) {
      this.exportSlideToJpg(slideIndex).then(() => {
        this.$message.success('已下载')
      }).catch(err => {
        this.$message.warning(err.message || '导出失败')
      })
    },
    copySlideToClipboard(slideIndex) {
      if (!navigator.clipboard || !navigator.clipboard.write) {
        this.$message.warning('当前浏览器不支持复制图片，请使用「下载」后粘贴')
        return
      }
      this.exportSlideToBlob(slideIndex, 'image/png', 1)
        .then(blob => navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]))
        .then(() => this.$message.success('已复制到剪贴板'))
        .catch(err => this.$message.warning(err.message || '复制失败'))
    },
    copyTextToClipboard(text) {
      if (!text || typeof text !== 'string') return Promise.reject(new Error('无内容'))
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text)
      }
      return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '0'
        textarea.setAttribute('readonly', '')
        document.body.appendChild(textarea)
        textarea.select()
        textarea.setSelectionRange(0, text.length)
        try {
          const ok = document.execCommand('copy')
          document.body.removeChild(textarea)
          if (ok) resolve()
          else reject(new Error('execCommand copy 失败'))
        } catch (e) {
          document.body.removeChild(textarea)
          reject(e)
        }
      })
    },
    copySlideBase64(slideIndex) {
      this.exportSlideToBlob(slideIndex, 'image/png', 1)
        .then(blob => {
          return new Promise((resolve, reject) => {
            const fr = new FileReader()
            fr.onload = () => resolve(fr.result)
            fr.onerror = () => reject(new Error('读取失败'))
            fr.readAsDataURL(blob)
          })
        })
        .then(dataUrl => this.copyTextToClipboard(dataUrl))
        .then(() => this.$message.success('已复制 Base64 到剪贴板'))
        .catch(err => this.$message.warning(err.message || '复制失败'))
    },
    getPreviewHeight(slideIndex) {
      const ref = this.$refs['slideImg' + slideIndex]
      const el = Array.isArray(ref) ? ref[0] : ref
      const h = (el && el.clientHeight > 0) ? el.clientHeight : 0
      if (h > 0) this.slidePreviewHeights[slideIndex] = h
      return h || this.slidePreviewHeights[slideIndex] || 520
    },
    exportSlideToBlob(slideIndex, mimeType, quality) {
      const slide = this.slides[slideIndex]
      const imageUrl = this.fullUrl(slide.url)
      if (!imageUrl) return Promise.reject(new Error('图片地址无效'))
      const actualPreviewHeight = this.getPreviewHeight(slideIndex)
      const isDataOrBlob = imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')
      const loadImage = isDataOrBlob
        ? Promise.resolve(imageUrl)
        : this.$axios.get(imageUrl, { responseType: 'blob' }).then(res => {
            const blob = res.data
            if (!blob || !blob.size) return Promise.reject(new Error('图片加载失败'))
            return URL.createObjectURL(blob)
          })
      return loadImage.then(url => {
        return new Promise((resolve, reject) => {
          const img = new Image()
          img.crossOrigin = isDataOrBlob ? null : 'anonymous'
          img.onload = () => {
            if (!isDataOrBlob && url) URL.revokeObjectURL(url)
            try {
              const canvas = document.createElement('canvas')
              canvas.width = img.naturalWidth
              canvas.height = img.naturalHeight
              const ctx = canvas.getContext('2d')
              ctx.drawImage(img, 0, 0)
              const w = canvas.width
              const h = canvas.height
              const scale = h / actualPreviewHeight
              slide.boxes.forEach(box => {
                if (!box.content) return
                const exportFontSize = Math.round(box.fontSize * scale)
                const lineHeight = exportFontSize * 1.2
                const exportStrokeWidth = Math.max(0, (box.strokeWidth || 0) * scale)
                const exportShadowBlur = (box.shadowBlur || 0) * scale
                const exportShadowX = (box.shadowOffsetX || 0) * scale
                const exportShadowY = (box.shadowOffsetY || 0) * scale
                ctx.font = `${box.italic ? 'italic ' : ''}${box.bold ? 'bold ' : ''}${exportFontSize}px ${box.fontFamily}`
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                const x = (box.xPercent / 100) * w
                const y = (box.yPercent / 100) * h
                const lines = String(box.content).split('\n')
                const startY = y - (lines.length - 1) * lineHeight / 2
                const isGradient = (box.colorMode === 'gradient' || box.colorGradient) && box.gradientStart != null && box.gradientEnd != null
                if (isGradient) {
                  const dir = box.gradientDirection || 'to right'
                  const d = Math.max(w, h) * 0.5
                  let x1, y1, x2, y2
                  if (dir === 'to right') { x1 = x - d; y1 = y; x2 = x + d; y2 = y }
                  else if (dir === 'to left') { x1 = x + d; y1 = y; x2 = x - d; y2 = y }
                  else if (dir === 'to bottom') { x1 = x; y1 = y - d; x2 = x; y2 = y + d }
                  else if (dir === 'to top') { x1 = x; y1 = y + d; x2 = x; y2 = y - d }
                  else if (dir === 'to bottom right') { x1 = x - d; y1 = y - d; x2 = x + d; y2 = y + d }
                  else { x1 = x + d; y1 = y + d; x2 = x - d; y2 = y - d }
                  const grd = ctx.createLinearGradient(x1, y1, x2, y2)
                  grd.addColorStop(0, box.gradientStart)
                  grd.addColorStop(1, box.gradientEnd)
                  ctx.fillStyle = grd
                } else {
                  ctx.fillStyle = box.color || '#000000'
                }
                if (exportShadowBlur > 0) {
                  ctx.shadowColor = box.shadowColor || 'rgba(0,0,0,0.5)'
                  ctx.shadowBlur = exportShadowBlur
                  ctx.shadowOffsetX = exportShadowX
                  ctx.shadowOffsetY = exportShadowY
                }
                lines.forEach((line, i) => {
                  const ly = startY + i * lineHeight
                  if (exportStrokeWidth > 0 && box.strokeColor) {
                    ctx.strokeStyle = box.strokeColor
                    ctx.lineWidth = exportStrokeWidth
                    ctx.strokeText(line, x, ly)
                  }
                  ctx.fillText(line, x, ly)
                })
                ctx.shadowColor = 'transparent'
                ctx.shadowBlur = 0
                ctx.shadowOffsetX = 0
                ctx.shadowOffsetY = 0
              })
              canvas.toBlob(blobOut => {
                if (blobOut) resolve(blobOut)
                else reject(new Error('导出失败'))
              }, mimeType, quality)
            } catch (e) {
              reject(e)
            }
          }
          img.onerror = () => {
            if (!isDataOrBlob && url) URL.revokeObjectURL(url)
            reject(new Error('图片加载失败'))
          }
          img.src = url
        })
      }).catch(err => Promise.reject(err.response ? new Error('图片加载失败') : err))
    },
    exportSlideToJpg(slideIndex) {
      return this.exportSlideToBlob(slideIndex, 'image/jpeg', 0.92).then(blob => {
        const a = document.createElement('a')
        a.download = `${this.editProject.name}_第${slideIndex + 1}页.jpg`
        a.href = URL.createObjectURL(blob)
        a.click()
        URL.revokeObjectURL(a.href)
      })
    },
    onSlideImgLoad(slideIndex) {
      this.$nextTick(() => {
        const h = this.getPreviewHeight(slideIndex)
        if (h > 0) this.slidePreviewHeights[slideIndex] = h
      })
    },
    previewSizeStyle(slideIndex) {
      return { maxWidth: '100%', maxHeight: '520px' }
    },
    overlayBoxStyle(box, slideIndex, isFullscreen) {
      const fontScale = (isFullscreen && this.fullscreenFontScale > 0) ? this.fullscreenFontScale : 1
      const fontSize = Math.round((box.fontSize || 28) * fontScale)
      const base = {
        position: 'absolute',
        left: box.xPercent + '%',
        top: box.yPercent + '%',
        transform: 'translate(-50%, -50%)',
        fontSize: fontSize + 'px',
        fontFamily: box.fontFamily,
        fontWeight: box.bold ? 'bold' : 'normal',
        fontStyle: box.italic ? 'italic' : 'normal',
        whiteSpace: 'pre',
        textAlign: 'center',
        pointerEvents: 'none',
        overflow: 'visible',
        backgroundColor: 'transparent',
        background: 'none'
      }
      if (this.isTextGradient(box)) {
        base.color = 'transparent'
      } else {
        base.color = box.color || '#000000'
      }
      if ((box.strokeWidth || 0) > 0 && box.strokeColor) {
        base.WebkitTextStroke = `${Math.round((box.strokeWidth || 0) * fontScale)}px ${box.strokeColor}`
      }
      const sb = box.shadowBlur != null ? box.shadowBlur : 0
      if (sb > 0) {
        const sx = (box.shadowOffsetX != null ? box.shadowOffsetX : 0) * fontScale
        const sy = (box.shadowOffsetY != null ? box.shadowOffsetY : 0) * fontScale
        const sbScaled = sb * fontScale
        const sc = box.shadowColor || 'rgba(0,0,0,0.5)'
        base.textShadow = `${sx}px ${sy}px ${sbScaled}px ${sc}`
      }
      return base
    },
    overlayTextGradientStyle(box, isFullscreen) {
      const fontScale = (isFullscreen && this.fullscreenFontScale > 0) ? this.fullscreenFontScale : 1
      const o = {
        background: `linear-gradient(${box.gradientDirection || 'to right'}, ${box.gradientStart}, ${box.gradientEnd})`,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent'
      }
      if ((box.strokeWidth || 0) > 0 && box.strokeColor) {
        o.WebkitTextStroke = `${Math.round((box.strokeWidth || 0) * fontScale)}px ${box.strokeColor}`
      }
      const sb = box.shadowBlur != null ? box.shadowBlur : 0
      if (sb > 0) {
        const sx = (box.shadowOffsetX != null ? box.shadowOffsetX : 0) * fontScale
        const sy = (box.shadowOffsetY != null ? box.shadowOffsetY : 0) * fontScale
        const sbScaled = sb * fontScale
        const sc = box.shadowColor || 'rgba(0,0,0,0.5)'
        o.textShadow = `${sx}px ${sy}px ${sbScaled}px ${sc}`
      }
      return o
    },
  }
}
</script>

<style lang="less" scoped>
.ppt-slides {
  padding: 20px 0;
  max-width: 100%;
}
.ppt-header, .ppt-edit-header {
  margin-bottom: 16px;
  text-align: center;
}
.ppt-edit-header {
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;
}
.ppt-title, .ppt-edit-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.ppt-title i { color: #409EFF; }
.ppt-desc {
  font-size: 13px;
  color: #606266;
  margin: 8px 0 0 0;
}
.ppt-list-wrap {
  min-height: 200px;
}
.ppt-list-toolbar {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
}
.ppt-search-input {
  width: min(420px, 100%);
}
.ppt-search-input :deep(.el-input__prefix) {
  display: flex;
  align-items: center;
  height: 100%;
}
.ppt-search-input :deep(.el-input__prefix-inner) {
  display: inline-flex;
  align-items: center;
  height: 100%;
}
.ppt-search-input :deep(.el-input__icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  line-height: 1;
}
.ppt-list-scroll {
  max-height: 70vh;
  overflow-y: auto;
}
.ppt-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}
.ppt-list-item {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  background: #f5f7fa;
  transition: box-shadow 0.2s;
}
.ppt-list-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}
.ppt-list-item img {
  width: 100%;
  height: 140px;
  object-fit: contain;
  object-position: center;
  display: block;
  background: #fff;
}
.ppt-list-name {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  font-size: 13px;
  color: #303133;
  text-align: center;
}
.ppt-list-icon {
  font-size: 14px;
  color: #409EFF;
}
.ppt-load-more, .ppt-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  margin: 16px 0;
}
.ppt-edit-body {
  margin-top: 16px;
}
.ppt-slide-tabs {
  margin-bottom: 0;
}
.ppt-slide-tabs .el-tabs__content {
  overflow: visible;
}
.ppt-slide-block {
  margin-bottom: 32px;
  padding: 16px;
  background: #fafbfc;
  border-radius: 10px;
  border: 1px solid #e4e7ed;
}
.ppt-slide-label {
  font-size: 15px;
  margin: 0 0 12px 0;
  color: #303133;
}
.ppt-slide-row {
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
  align-items: stretch;
  min-height: 560px;
}
.ppt-preview-wrap {
  flex: 0 0 50%;
  width: 50%;
  max-width: none;
  min-width: 0;
  box-sizing: border-box;
}
.ppt-preview-wrap-split {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}
.ppt-preview-wrap-split .ppt-download-row {
  flex-shrink: 0;
}
.ppt-preview-split {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ppt-cover-example {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
}
.ppt-cover-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
  flex-shrink: 0;
}
.ppt-preview-img-half {
  display: block;
  width: 100%;
  max-height: 180px;
  height: auto;
  object-fit: contain;
  object-position: center top;
  cursor: pointer;
}
.ppt-preview-inner-half {
  flex: 0 0 auto;
  position: relative;
  overflow: hidden;
}
.ppt-preview-wrap-split .ppt-preview-inner-half .ppt-preview-img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
}
.ppt-preview-inner {
  position: relative;
  display: block;
  width: 100%;
  overflow: hidden;
}
.ppt-preview-img {
  display: block;
  width: 100%;
  height: auto;
  cursor: pointer;
}
.ppt-download-row {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.ppt-download-hint {
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.ppt-file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}
.ppt-text-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-width: none;
}
/* 渐变文字用 inline，避免整块显示为背景；仅文字笔画显示渐变 */
.ppt-text-overlay-inner {
  display: inline;
  font: inherit;
  white-space: pre;
  text-align: center;
  -webkit-background-clip: text;
  background-clip: text;
}
.ppt-form-wrap {
  flex: 0 0 50%;
  width: 50%;
  min-width: 0;
  max-width: none;
  box-sizing: border-box;
}
.ppt-box-count {
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}
.ppt-box-tabs {
  margin-top: 8px;
}
.ppt-box-tabs .el-tabs__content {
  overflow: visible;
}
.ppt-box-tabs .el-tabs__content .el-tab-pane {
  overflow: visible;
}
.ppt-box-form {
  margin-bottom: 20px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}
.ppt-box-form h5 {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #606266;
}
.option-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}
.option-label {
  width: 88px;
  font-size: 13px;
  color: #606266;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}
.option-label i {
  color: #409EFF;
  font-size: 14px;
}
.option-row-slider .slider-wrap {
  flex: 1;
  min-width: 200px;
  max-width: 100%;
}
.option-row-presets {
  overflow: visible;
  align-items: flex-start;
}
.option-row-presets .preset-list {
  display: grid;
  grid-template-columns: repeat(9, 36px);
  gap: 8px;
  flex: 1;
  min-width: 0;
  width: fit-content;
  overflow: visible;
}
.preset-item {
  width: 36px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.15s, box-shadow 0.15s;
  position: relative;
  z-index: 1;
  pointer-events: auto;
}
.preset-item:hover {
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.input-color {
  width: 96px !important;
  margin-left: 6px !important;
}
.input-select {
  width: 140px !important;
}
.ppt-input-text {
  flex: 1;
  min-width: 160px;
}
.ppt-font-select {
  flex: 1 1 200px;
  min-width: 160px;
  max-width: 100%;
}
.ppt-preview-mask {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}
.ppt-fullscreen-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  max-height: 100%;
}
.ppt-fullscreen-inner {
  position: relative;
  display: inline-block;
  line-height: 0;
}
.ppt-fullscreen-inner .ppt-preview-full-img {
  display: block;
}
.ppt-text-overlay-fullscreen {
  pointer-events: none;
}
.ppt-preview-full-img {
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  cursor: pointer;
  border-radius: 4px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
.ppt-preview-close {
  margin-top: 16px;
}

@media (max-width: 768px) {
  .ppt-slides { padding: 12px 10px; }
  .ppt-header, .ppt-edit-header { margin-bottom: 12px; }
  .ppt-title { font-size: 18px; }
  .ppt-list-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 12px; }
  .ppt-list-item img { height: 120px; }
  .ppt-slide-block { padding: 12px 10px; margin-bottom: 20px; width: 100%; box-sizing: border-box; }
  .ppt-slide-row {
    flex-direction: column;
    flex-wrap: wrap;
    min-height: 0;
    width: 100%;
  }
  .ppt-preview-wrap {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex: 0 0 auto;
  }
  .ppt-download-row { flex-wrap: wrap; gap: 8px; }
  .ppt-form-wrap {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    flex: 1 1 auto;
    box-sizing: border-box;
  }
  .option-row-presets .preset-list { grid-template-columns: repeat(6, 36px); }
  .option-row-slider .slider-wrap { min-width: 120px; max-width: 100%; }
  .ppt-box-form { padding: 10px; }
  .ppt-slide-tabs::v-deep .el-tabs__content,
  .ppt-slide-tabs::v-deep .el-tab-pane { width: 100%; }
}
@media (max-width: 480px) {
  .ppt-slides { padding: 10px 8px; }
  .ppt-title { font-size: 16px; }
  .ppt-list-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
  .ppt-list-item img { height: 100px; }
  .ppt-slide-block { padding: 10px 8px; }
  .ppt-download-row .el-button { font-size: 12px; padding: 7px 10px; }
  .option-label { width: 72px; font-size: 12px; }
  .option-row-presets .preset-list { grid-template-columns: repeat(5, 34px); gap: 6px; }
  .preset-item { width: 34px; height: 26px; }
  .ppt-box-form { padding: 8px; }
  .ppt-box-form h5 { font-size: 12px; }
}
</style>

<style lang="less">
.dark-mode .ppt-slides {
  .ppt-title, .ppt-edit-title { color: var(--text-primary); }
  .ppt-desc { color: var(--text-regular); }
  .ppt-list-item {
    border-color: var(--border-color);
    background: var(--bg-secondary);
  }
  .ppt-list-name { color: var(--text-primary); }
  .ppt-empty, .ppt-load-more { color: var(--text-secondary); }
  .ppt-slide-block {
    background: var(--bg-secondary);
    border-color: var(--border-color);
  }
  .ppt-slide-label { color: var(--text-primary); }
  .ppt-cover-label { color: var(--text-secondary); }
  .ppt-box-form {
    background: var(--bg-card, var(--bg-secondary));
    border-color: var(--border-color);
  }
  .ppt-box-form h5 { color: var(--text-regular); }
  .ppt-box-count { color: var(--text-regular); }
  .option-label { color: var(--text-regular); }
  .option-label i { color: #409eff; }
  .ppt-download-hint { color: var(--text-secondary); }
  .preset-item { border-color: var(--border-color); }
  .ppt-preview-img { border-radius: 4px; }
}
</style>
